import { useCallback, useEffect, useMemo, useState } from "react";
import type { ReactElement } from "react";

import { useAdminAuth } from "@/hooks/useAdminAuth";
import { formatDateTime } from "@/lib/format";
import { glopicApi } from "@/lib/glopic-api";

interface ConfigItem {
  config_key: string;
  config_json: unknown;
  version: number;
  sha256: string;
  updated_by: string | null;
  change_note: string | null;
  updated_at: string;
}

interface ConfigDomain {
  key: string;
  label: string;
  icon: string;
}

interface ConfigRuntimeStatus {
  bundle_id: string;
  cache_state: "warm" | "cold_db_fallback" | "degraded_db_only";
  publishable: boolean;
}

interface GatewayCapabilities {
  config_domains: string[];
  initializable_config_domains: string[];
  image_providers: string[];
  absent_snapshot: { version: 0; sha256: string };
}

const configDomainLabels = [
  { key: "glopic_meta", label: "应用元信息", icon: "ri-information-line" },
  { key: "glopic_templates", label: "模板配置", icon: "ri-layout-masonry-line" },
  { key: "glopic_styles", label: "风格配置", icon: "ri-palette-line" },
  { key: "glopic_display", label: "展示配置", icon: "ri-tv-line" },
  { key: "glopic_curated_gallery", label: "官方图库", icon: "ri-gallery-line" },
  { key: "glopic_models", label: "模型配置", icon: "ri-cpu-line" },
  { key: "glopic_generation_runtime", label: "生图执行策略", icon: "ri-flow-chart" },
  { key: "glopic_prompts", label: "Prompt 与 Agent", icon: "ri-chat-ai-line" },
  { key: "glopic_prompt_assets", label: "Prompt 资产", icon: "ri-stack-line" },
  { key: "glopic_merchant", label: "商家端配置", icon: "ri-store-2-line" },
  { key: "glopic_points", label: "积分配置", icon: "ri-coins-line" },
  { key: "glopic_agreements", label: "协议配置", icon: "ri-file-list-3-line" },
  { key: "glopic_safety", label: "安全配置", icon: "ri-shield-check-line" },
  { key: "glopic_contact", label: "联系方式", icon: "ri-customer-service-2-line" },
  { key: "glopic_test_accounts", label: "测试白名单", icon: "ri-shield-user-line" },
] satisfies ConfigDomain[];

function getConfigDomains(
  capabilities: GatewayCapabilities | null,
  configs: ConfigItem[],
): ConfigDomain[] {
  if (!capabilities) return [];
  const labelsByKey = new Map(configDomainLabels.map((domain) => [domain.key, domain]));
  const visibleKeys = capabilities.config_domains.filter((key) => (
    configs.some((config) => config.config_key === key)
    || capabilities.initializable_config_domains.includes(key)
  ));
  return visibleKeys.map((key) => (
    labelsByKey.get(key) ?? { key, label: key, icon: "ri-settings-3-line" }
  ));
}

function prettyJson(value: unknown): string {
  if (typeof value !== "string") return JSON.stringify(value, null, 2);
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
}

function selectConfig(items: ConfigItem[], preferredKey?: string): ConfigItem | undefined {
  if (preferredKey) {
    const preferred = items.find((item) => item.config_key === preferredKey);
    if (preferred) return preferred;
  }
  return items.find((item) => item.config_key === "glopic_meta") ?? items[0];
}

function getApiErrorMessage(error: unknown): string {
  const fallback = "配置发布失败，请稍后重试";
  if (!error || typeof error !== "object") return fallback;
  const response = (error as { response?: { status?: number; data?: unknown } }).response;
  const data = response?.data;
  const detail = data && typeof data === "object"
    ? (data as { detail?: unknown; error?: { code?: unknown } }).detail
    : null;
  const nestedCode = data && typeof data === "object"
    ? (data as { error?: { code?: unknown } }).error?.code
    : null;
  const code = typeof nestedCode === "string"
    ? nestedCode
    : typeof detail === "string"
      ? detail.split(":", 1)[0]
      : detail && typeof detail === "object" && "code" in detail
        ? String((detail as { code: unknown }).code)
        : "";
  const safeMessages: Record<string, string> = {
    CONFIG_001: "配置校验未通过，请检查 JSON 内容",
    AUDIT_001: "审计服务暂不可用，配置未发布",
    RISK_001: "生产环境操作需要二次确认",
    RISK_INVALID: "二次确认已失效，请重新确认",
    RISK_EXPIRED: "二次确认已过期，请重新确认",
    SYS_001: fallback,
  };
  if (safeMessages[code]) return safeMessages[code];
  if (response?.status === 403) return "当前账号无配置发布权限";
  return fallback;
}

function JsonPreview({ jsonStr }: { jsonStr: string }) {
  const parsed = useMemo(() => {
    try {
      return { valid: true as const, value: JSON.parse(jsonStr) as unknown };
    } catch {
      return { valid: false as const, value: null };
    }
  }, [jsonStr]);

  if (!parsed.valid) {
    return <div className="font-mono text-sm text-red-500">JSON 格式错误，无法预览</div>;
  }

  const renderValue = (value: unknown, indent: number): ReactElement => {
    const pad = " ".repeat(indent * 2);
    if (value === null) return <span className="text-gray-400">null</span>;
    if (typeof value === "boolean") return <span className="text-amber-600">{String(value)}</span>;
    if (typeof value === "number") return <span className="text-emerald-600">{value}</span>;
    if (typeof value === "string") return <span className="text-rose-500">"{value}"</span>;
    if (Array.isArray(value)) {
      if (value.length === 0) return <span className="text-text-primary">[]</span>;
      return (
        <span>
          <span className="text-text-primary">[</span>{"\n"}
          {value.map((item, index) => (
            <span key={index}>
              {pad}{"  "}{renderValue(item, indent + 1)}
              {index < value.length - 1 ? <span className="text-text-primary">,</span> : null}
              {"\n"}
            </span>
          ))}
          {pad}<span className="text-text-primary">]</span>
        </span>
      );
    }
    if (typeof value === "object") {
      const entries = Object.entries(value as Record<string, unknown>);
      if (entries.length === 0) return <span className="text-text-primary">{"{}"}</span>;
      return (
        <span>
          <span className="text-text-primary">{"{"}</span>{"\n"}
          {entries.map(([key, item], index) => (
            <span key={key}>
              {pad}{"  "}<span className="text-indigo-500">"{key}"</span>
              <span className="text-text-primary">: </span>{renderValue(item, indent + 1)}
              {index < entries.length - 1 ? <span className="text-text-primary">,</span> : null}
              {"\n"}
            </span>
          ))}
          {pad}<span className="text-text-primary">{"}"}</span>
        </span>
      );
    }
    return <span>{String(value)}</span>;
  };

  return (
    <pre className="whitespace-pre-wrap font-mono text-sm leading-relaxed">
      {renderValue(parsed.value, 0)}
    </pre>
  );
}

export default function AiConfigPage() {
  const { hasRole } = useAdminAuth();
  const canPublish = hasRole(["super_admin"]);
  const [configs, setConfigs] = useState<ConfigItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [activeTab, setActiveTab] = useState("glopic_meta");
  const [editingJson, setEditingJson] = useState("");
  const [changeNote, setChangeNote] = useState("");
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [auditDeferred, setAuditDeferred] = useState(false);
  const [parseError, setParseError] = useState("");
  const [configRuntimeStatus, setConfigRuntimeStatus] = useState<ConfigRuntimeStatus | null>(null);
  const [gatewayCapabilities, setGatewayCapabilities] = useState<GatewayCapabilities | null>(null);
  const [controlPlaneError, setControlPlaneError] = useState("");

  const fetchConfigs = useCallback(async (preferredKey?: string) => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await glopicApi.get("/config");
      const data = response.data.data;
      const items: ConfigItem[] = Array.isArray(data)
        ? data
        : Object.entries(data as Record<string, Omit<ConfigItem, "config_key">>).map(
          ([key, value]) => ({ config_key: key, ...value }),
        );
      setConfigs(items);
      const selected = selectConfig(items, preferredKey);
      if (selected) {
        setActiveTab(selected.config_key);
        setEditingJson(prettyJson(selected.config_json));
      }
    } catch {
      setLoadError("配置加载失败，请刷新后重试");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchControlPlaneStatus = useCallback(async () => {
    try {
      const [runtimeResponse, capabilityResponse] = await Promise.all([
        glopicApi.get("/config/runtime-status"),
        glopicApi.get("/config/gateway-capabilities"),
      ]);
      setConfigRuntimeStatus(runtimeResponse.data.data as ConfigRuntimeStatus);
      setGatewayCapabilities(capabilityResponse.data.data as GatewayCapabilities);
      setControlPlaneError("");
    } catch {
      setControlPlaneError("配置缓存或统一网关状态不可用，禁止发布配置");
    }
  }, []);

  useEffect(() => {
    fetchConfigs();
    fetchControlPlaneStatus();
  }, [fetchConfigs, fetchControlPlaneStatus]);

  const currentConfig = configs.find((config) => config.config_key === activeTab);
  const availableDomains = getConfigDomains(gatewayCapabilities, configs);
  const currentSnapshot = currentConfig
    ? { version: currentConfig.version, sha256: currentConfig.sha256 }
    : gatewayCapabilities?.initializable_config_domains.includes(activeTab)
      ? gatewayCapabilities.absent_snapshot
      : null;
  const currentDomain = availableDomains.find((domain) => domain.key === activeTab);
  const isCachePublishable = configRuntimeStatus?.publishable === true;
  const isValidJson = useMemo(() => {
    try {
      JSON.parse(editingJson);
      return true;
    } catch {
      return false;
    }
  }, [editingJson]);

  const handleTabChange = (key: string) => {
    const config = configs.find((item) => item.config_key === key);
    if (config) {
      setActiveTab(key);
      setEditingJson(prettyJson(config.config_json));
    } else if (gatewayCapabilities?.initializable_config_domains.includes(key)) {
      setActiveTab(key);
      setEditingJson("{}\n");
    } else {
      return;
    }
    setChangeNote("");
    setParseError("");
    setSaveSuccess(false);
    setAuditDeferred(false);
  };

  const handleSave = async () => {
    setParseError("");
    setSaveSuccess(false);
    setAuditDeferred(false);
    if (!canPublish) {
      setParseError("当前账号仅可查看配置，发布需要超级管理员权限");
      return;
    }
    if (!changeNote.trim()) {
      setParseError("请填写变更备注，配置发布审计必须记录原因");
      return;
    }

    let parsed: unknown;
    try {
      parsed = JSON.parse(editingJson);
    } catch {
      setParseError("JSON 格式错误，请检查语法");
      return;
    }
    if (!currentSnapshot) {
      setParseError("配置版本不存在，请刷新后重试");
      return;
    }

    setSaveLoading(true);
    const savedKey = activeTab;
    try {
      await glopicApi.post("/config/validate", {
        config_key: savedKey,
        config_json: parsed,
        expected_version: currentSnapshot.version,
        expected_sha256: currentSnapshot.sha256,
      });
      const response = await glopicApi.put(`/config/${savedKey}`, {
        config_json: parsed,
        change_note: changeNote.trim(),
        expected_version: currentSnapshot.version,
        expected_sha256: currentSnapshot.sha256,
      });
      setAuditDeferred(response.data?.data?.admin_audit_state === "deferred");
      setSaveSuccess(response.data?.data?.admin_audit_state !== "deferred");
      await fetchConfigs(savedKey);
      await fetchControlPlaneStatus();
    } catch (error) {
      setParseError(getApiErrorMessage(error));
    } finally {
      setSaveLoading(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-text-primary">应用配置</h2>
        <p className="mt-1 text-sm text-text-secondary">
          管理 NananoImage2 已授权配置域
        </p>
      </div>

      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-surface-border bg-white p-4">
          <div className="text-sm font-medium text-text-primary">配置缓存</div>
          <p className={`mt-1 text-xs ${
            configRuntimeStatus?.publishable === true ? "text-emerald-600" : "text-amber-600"
          }`}>
            {configRuntimeStatus
              ? `${configRuntimeStatus.cache_state} · Bundle ${configRuntimeStatus.bundle_id}`
              : "读取中"}
          </p>
        </div>
        <div className="rounded-xl border border-surface-border bg-white p-4">
          <div className="text-sm font-medium text-text-primary">统一 AI Gateway</div>
          <p className="mt-1 text-xs text-text-secondary">
            {gatewayCapabilities
              ? `Provider: ${gatewayCapabilities.image_providers.join(", ") || "无"}`
              : "读取中"}
          </p>
        </div>
      </div>
      {controlPlaneError ? <p className="text-xs text-red-500">{controlPlaneError}</p> : null}
      {!controlPlaneError && configRuntimeStatus && !isCachePublishable ? (
        <p className="text-xs text-amber-600">配置缓存或发布围栏状态异常，恢复前禁止发布配置。</p>
      ) : null}
      {!controlPlaneError && configRuntimeStatus?.cache_state === "cold_db_fallback" ? (
        <p className="text-xs text-text-secondary">缓存已自然过期，当前由数据库安全回源；发布时会重新预热缓存。</p>
      ) : null}

      <div className="flex items-center gap-1 overflow-x-auto rounded-xl border border-surface-border bg-white p-1">
        {availableDomains.map((domain) => {
          return (
            <button
              key={domain.key}
              type="button"
              onClick={() => handleTabChange(domain.key)}
              className={`flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                activeTab === domain.key
                  ? "bg-brand text-white"
                  : "text-text-secondary hover:bg-surface"
              }`}
            >
              <i className={`${domain.icon} text-sm`} />
              {domain.label}
            </button>
          );
        })}
      </div>

      {loading ? (
        <div className="rounded-xl border border-surface-border bg-white p-10 text-center">
          <i className="ri-loader-4-line mb-2 block animate-spin text-2xl text-brand" />
          <p className="text-sm text-text-muted">加载配置中...</p>
        </div>
      ) : loadError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-500">
          {loadError}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-surface-border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-surface-border px-5 py-4">
            <div>
              <h3 className="text-sm font-medium text-text-primary">
                {currentDomain?.label || activeTab}
              </h3>
              {currentConfig ? (
                <p className="mt-0.5 text-xs text-text-muted">
                  版本 v{currentConfig.version} · 更新于 {formatDateTime(currentConfig.updated_at)}
                  {currentConfig.updated_by ? ` · ${currentConfig.updated_by}` : ""}
                  {currentConfig.change_note ? ` · ${currentConfig.change_note}` : ""}
                </p>
              ) : currentSnapshot ? (
                <p className="mt-0.5 text-xs text-amber-600">
                  尚未发布 · 首次保存将使用版本 0 的缺失快照执行 CAS 初始化
                </p>
              ) : null}
            </div>
            <div className="flex items-center gap-2">
              {saveSuccess ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-500">
                  <i className="ri-checkbox-circle-line" />保存成功
                </span>
              ) : null}
              {auditDeferred ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-600">
                  <i className="ri-time-line" />已发布，审计待补
                </span>
              ) : null}
              <button
                type="button"
                onClick={handleSave}
                disabled={!canPublish || !isCachePublishable || saveLoading || !isValidJson || Boolean(controlPlaneError)}
                className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-xl bg-brand px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-brand/90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {saveLoading ? <><i className="ri-loader-2-line animate-spin" />保存中...</> : <><i className="ri-save-line" />保存</>}
              </button>
            </div>
          </div>

          <div className="space-y-4 p-5">
            {!canPublish ? (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-700">
                当前账号仅可查看配置，发布需要超级管理员权限。
              </div>
            ) : null}
            {parseError ? (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-3">
                <i className="ri-error-warning-line text-red-500" />
                <span className="text-sm text-red-500">{parseError}</span>
              </div>
            ) : null}
            <div>
              <label className="mb-1.5 block text-sm text-text-secondary">变更备注</label>
              <input
                type="text"
                value={changeNote}
                onChange={(event) => setChangeNote(event.target.value)}
                readOnly={!canPublish}
                placeholder="描述本次变更内容（必填，将写入不可变审计）"
                className="h-10 w-full rounded-lg border border-surface-border bg-surface px-3 text-sm text-text-primary placeholder:text-text-muted focus:border-brand/50 focus:outline-none read-only:cursor-not-allowed read-only:opacity-70"
              />
            </div>
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm text-text-secondary">JSON 编辑</label>
                <textarea
                  aria-label="配置 JSON"
                  value={editingJson}
                  onChange={(event) => {
                    setEditingJson(event.target.value);
                    setParseError("");
                  }}
                  readOnly={!canPublish}
                  rows={24}
                  className="w-full resize-y rounded-lg border border-surface-border bg-surface px-3 py-3 font-mono text-sm leading-relaxed text-text-primary focus:border-brand/50 focus:outline-none read-only:cursor-not-allowed read-only:opacity-70"
                  spellCheck={false}
                />
              </div>
              <div>
                <div className="mb-1.5 text-sm text-text-secondary">JSON 预览</div>
                <div aria-label="JSON 预览" className="max-h-[600px] min-h-[600px] overflow-auto rounded-lg border border-surface-border bg-surface px-3 py-3">
                  <JsonPreview jsonStr={editingJson} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
